import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Menu,
  Progress,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCalendarEvent,
  IconChevronDown,
  IconCircleCheck,
  IconClockHour4,
  IconFileText,
  IconInbox,
  IconPlus,
  IconRefresh,
  IconReportMoney,
  IconUserCog,
} from "@tabler/icons-react";
import { useAuth } from "../auth/AuthContext";
import { dashboardApi, type DashboardOverview, type StatusCount } from "../api/dashboard";
import { formatCurrency } from "../utils/currency";
import { dhivehiDisplayStyle } from "../utils/dhivehiText";
import {
  EmptyState,
  KpiTile,
  SectionCard,
  StatusBadge,
  statusColor,
  statusLabel,
  tabular,
  type Kpi,
} from "../components/DashboardParts";

dayjs.extend(relativeTime);

const ROLE_LABELS: Record<string, string> = {
  ADMIN: "Administrator",
  PROCUREMENT_OFFICER: "Procurement Officer",
  HEAD_OF_PROCUREMENT: "Head of Procurement",
  BID_COMMITTEE_MEMBER: "Bid Committee Member",
  CEO: "CEO",
  VIEWER: "Viewer",
};

function buildKpis(data: DashboardOverview): Kpi[] {
  const kpis: Kpi[] = [];
  const { myPending, contracts, upcomingMeetings, pipeline, role } = data;

  if (role !== "VIEWER") {
    kpis.push({
      label: "Waiting on you",
      value: String(myPending.total),
      hint:
        myPending.total === 0
          ? "You're all caught up"
          : `Across ${myPending.items.length} ${myPending.items.length === 1 ? "queue" : "queues"}`,
      to: "/tenders/pending-approvals",
      icon: IconInbox,
      color: myPending.total > 0 ? "orange" : "teal",
    });
  }

  if (contracts) {
    const consumedPct = contracts.activeValue > 0 ? (contracts.consumedValue / contracts.activeValue) * 100 : 0;
    kpis.push(
      {
        label: "Active contract value",
        value: formatCurrency(contracts.activeValue),
        hint: `${consumedPct.toFixed(0)}% consumed · ${formatCurrency(contracts.remainingValue)} remaining`,
        to: "/contracts",
        icon: IconReportMoney,
        color: "blue",
        progress: consumedPct,
      },
      {
        label: "Expiring in 90 days",
        value: String(contracts.expiringCount),
        hint:
          contracts.expiring[0]?.daysLeft != null
            ? `Next ends in ${contracts.expiring[0].daysLeft} days`
            : "No active contracts ending soon",
        to: "/contracts",
        icon: IconAlertTriangle,
        color: contracts.expiringCount > 0 ? "red" : "gray",
      }
    );
  } else {
    const awardSheets = pipeline.find((p) => p.key === "awardSheets");
    const awaiting = awardSheets?.statuses.find((s) => s.status === "PENDING")?.count ?? 0;
    kpis.push({
      label: "Award sheets in signing",
      value: String(awaiting),
      hint: "Sent to the committee for signatures",
      to: "/bid-committee/award-sheets",
      icon: IconFileText,
      color: "indigo",
    });
  }

  const next = upcomingMeetings[0];
  kpis.push({
    label: "Upcoming meetings",
    value: String(upcomingMeetings.length),
    hint: next ? `Next ${dayjs(next.plannedStart).format("ddd D MMM, HH:mm")}` : "None scheduled",
    to: "/bid-committee/meetings",
    icon: IconCalendarEvent,
    color: "cyan",
  });

  return kpis;
}

function PipelineRow({ label, path, statuses }: { label: string; path: string; statuses: StatusCount[] }) {
  const total = statuses.reduce((sum, s) => sum + s.count, 0);
  return (
    <Stack gap={6}>
      <Group justify="space-between">
        <Anchor component={Link} to={path} size="sm" fw={600} c="var(--mantine-color-text)">
          {label}
        </Anchor>
        <Text size="sm" c="dimmed" style={tabular}>
          {total} total
        </Text>
      </Group>
      {total === 0 ? (
        <Text size="xs" c="dimmed">
          No records yet
        </Text>
      ) : (
        <>
          <Progress.Root size={10} aria-label={`${label} by status`}>
            {statuses.map((s) => (
              <Tooltip key={s.status} label={`${statusLabel(s.status)}: ${s.count}`} withArrow>
                <Progress.Section value={(s.count / total) * 100} color={statusColor(s.status)} />
              </Tooltip>
            ))}
          </Progress.Root>
          <Group gap="md" wrap="wrap">
            {statuses.map((s) => (
              <Group key={s.status} gap={6} wrap="nowrap">
                <span
                  aria-hidden
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: `var(--mantine-color-${statusColor(s.status)}-filled)`,
                  }}
                />
                <Text size="xs" c="dimmed">
                  {statusLabel(s.status)}{" "}
                  <Text span size="xs" fw={600} c="var(--mantine-color-text)" style={tabular}>
                    {s.count}
                  </Text>
                </Text>
              </Group>
            ))}
          </Group>
        </>
      )}
    </Stack>
  );
}

function DashboardSkeleton() {
  return (
    <Stack gap="lg" aria-busy="true" aria-label="Loading dashboard">
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} h={126} radius="md" />
        ))}
      </SimpleGrid>
      <Grid>
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Skeleton h={280} radius="md" />
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Skeleton h={280} radius="md" />
        </Grid.Col>
      </Grid>
      <Skeleton h={320} radius="md" />
    </Stack>
  );
}

function PrimaryAction({ role }: { role: string }) {
  const navigate = useNavigate();
  if (role === "PROCUREMENT_OFFICER" || role === "HEAD_OF_PROCUREMENT" || role === "ADMIN") {
    return (
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <Button leftSection={<IconPlus size={16} />} rightSection={<IconChevronDown size={14} />}>
            New
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={() => navigate("/tenders/new")}>Tender</Menu.Item>
          <Menu.Item onClick={() => navigate("/single-source/new")}>Single source form</Menu.Item>
          <Menu.Item onClick={() => navigate("/project-summary-sheets/new")}>Project summary sheet</Menu.Item>
          <Menu.Item onClick={() => navigate("/contracts/new")}>Contract</Menu.Item>
        </Menu.Dropdown>
      </Menu>
    );
  }
  if (role === "BID_COMMITTEE_MEMBER" || role === "CEO") {
    return (
      <Button component={Link} to="/tenders/pending-approvals" leftSection={<IconInbox size={16} />}>
        Review approvals
      </Button>
    );
  }
  return null;
}

export const Dashboard: React.FC = () => {
  const { user, loading } = useAuth();
  const { data, isLoading, isError, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ["dashboardOverview"],
    queryFn: dashboardApi.getOverview,
    enabled: !!user,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  if (loading || !user) {
    return (
      <Container size="xl" py="xl">
        {loading ? <DashboardSkeleton /> : <Alert color="red">Not authenticated. Please log in.</Alert>}
      </Container>
    );
  }

  const role = user.role ?? "VIEWER";
  const greeting = dayjs().hour() < 12 ? "Good morning" : dayjs().hour() < 17 ? "Good afternoon" : "Good evening";
  const firstName = user.fullName?.split(" ")[0] ?? "";

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
          <Stack gap={4}>
            <Text size="sm" c="dimmed">
              {dayjs().format("dddd, D MMMM YYYY")} · {ROLE_LABELS[role] ?? role}
            </Text>
            <Title order={1} size="h2">
              {greeting}
              {firstName ? `, ${firstName}` : ""}
            </Title>
          </Stack>
          <Group gap="sm">
            {dataUpdatedAt > 0 && (
              <Text size="xs" c="dimmed" visibleFrom="sm">
                Updated {dayjs(dataUpdatedAt).format("HH:mm")}
              </Text>
            )}
            <Tooltip label="Refresh">
              <ActionIcon
                variant="default"
                size="lg"
                aria-label="Refresh dashboard"
                onClick={() => refetch()}
                loading={isFetching}
              >
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            {role === "ADMIN" && (
              <Button variant="default" component={Link} to="/admin/users" leftSection={<IconUserCog size={16} />}>
                Manage users
              </Button>
            )}
            <PrimaryAction role={role} />
          </Group>
        </Group>

        {isLoading && <DashboardSkeleton />}

        {isError && !data && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Couldn't load the dashboard">
            <Group gap="sm">
              <Text size="sm">Check your connection and try again.</Text>
              <Button size="xs" variant="light" color="red" onClick={() => refetch()}>
                Retry
              </Button>
            </Group>
          </Alert>
        )}

        {data && (
          <>
            {/* KPIs */}
            <SimpleGrid cols={{ base: 1, sm: 2, lg: buildKpis(data).length >= 4 ? 4 : 3 }}>
              {buildKpis(data).map((kpi) => (
                <KpiTile key={kpi.label} kpi={kpi} />
              ))}
            </SimpleGrid>

            <Grid gutter="lg">
              {/* Needs attention */}
              {role !== "VIEWER" && (
                <Grid.Col span={{ base: 12, lg: 7 }}>
                  <SectionCard title="Needs your attention" action={{ label: "All approvals", to: "/tenders/pending-approvals" }}>
                    {data.myPending.items.length === 0 && data.myReturned.length === 0 ? (
                      <EmptyState icon={IconCircleCheck} message="Nothing is waiting on you right now." />
                    ) : (
                      <Stack gap="xs">
                        {data.myPending.items.map((item) => (
                          <Card
                            key={item.key}
                            component={Link}
                            to={item.path}
                            withBorder
                            radius="sm"
                            padding="sm"
                            className="transition-colors duration-150 hover:bg-[var(--mantine-color-default-hover)]"
                          >
                            <Group justify="space-between" wrap="nowrap">
                              <Group gap="sm" wrap="nowrap">
                                <ThemeIcon variant="light" color="orange" radius="sm">
                                  <IconClockHour4 size={16} aria-hidden />
                                </ThemeIcon>
                                <Text size="sm" fw={500}>
                                  {item.label}
                                </Text>
                              </Group>
                              <Badge color="orange" variant="filled" radius="sm" style={tabular}>
                                {item.count}
                              </Badge>
                            </Group>
                          </Card>
                        ))}

                        {data.myReturned.length > 0 && (
                          <>
                            <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt="sm">
                              Your drafts &amp; returned forms
                            </Text>
                            {data.myReturned.map((item) => (
                              <Card
                                key={item.path}
                                component={Link}
                                to={item.path}
                                withBorder
                                radius="sm"
                                padding="sm"
                                className="transition-colors duration-150 hover:bg-[var(--mantine-color-default-hover)]"
                              >
                                <Group justify="space-between" wrap="nowrap" gap="sm">
                                  <Stack gap={2} style={{ minWidth: 0 }}>
                                    <Text size="xs" c="dimmed">
                                      {item.type} · {item.reference}
                                    </Text>
                                    <Text size="sm" fw={500} lineClamp={1} style={dhivehiDisplayStyle(item.title)}>
                                      {item.title}
                                    </Text>
                                  </Stack>
                                  <Stack gap={2} align="flex-end" style={{ flexShrink: 0 }}>
                                    <StatusBadge status={item.status} />
                                    <Text size="xs" c="dimmed">
                                      {dayjs(item.updatedAt).fromNow()}
                                    </Text>
                                  </Stack>
                                </Group>
                              </Card>
                            ))}
                          </>
                        )}
                      </Stack>
                    )}
                  </SectionCard>
                </Grid.Col>
              )}

              {/* Upcoming meetings */}
              <Grid.Col span={{ base: 12, lg: role === "VIEWER" ? 12 : 5 }}>
                <SectionCard title="Upcoming bid committee meetings" action={{ label: "Calendar", to: "/calendar" }}>
                  {data.upcomingMeetings.length === 0 ? (
                    <EmptyState icon={IconCalendarEvent} message="No meetings are scheduled." />
                  ) : (
                    <Stack gap="sm">
                      {data.upcomingMeetings.map((m) => {
                        const start = dayjs(m.plannedStart);
                        return (
                          <Group
                            key={m.id}
                            renderRoot={(props) => <Link to={`/bid-committee/meetings/${m.id}`} {...props} />}
                            wrap="nowrap"
                            gap="md"
                            p="xs"
                            className="rounded-md no-underline transition-colors duration-150 hover:bg-[var(--mantine-color-default-hover)]"
                            style={{ color: "inherit" }}
                          >
                            <Stack
                              gap={0}
                              align="center"
                              justify="center"
                              w={52}
                              h={52}
                              style={{
                                flexShrink: 0,
                                borderRadius: 8,
                                background: "var(--mantine-color-blue-light)",
                                color: "var(--mantine-color-blue-light-color)",
                              }}
                            >
                              <Text size="xs" fw={600} tt="uppercase" lh={1.2}>
                                {start.format("MMM")}
                              </Text>
                              <Text size="lg" fw={700} lh={1.1} style={tabular}>
                                {start.format("D")}
                              </Text>
                            </Stack>
                            <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                              <Text size="sm" fw={600} lineClamp={1}>
                                {m.title}
                              </Text>
                              <Text size="xs" c="dimmed" lineClamp={1}>
                                {start.format("ddd HH:mm")} · {m.location} · {m.agendaCount} agenda{" "}
                                {m.agendaCount === 1 ? "item" : "items"}
                              </Text>
                            </Stack>
                            <StatusBadge status={m.status} />
                          </Group>
                        );
                      })}
                    </Stack>
                  )}
                </SectionCard>
              </Grid.Col>

              {/* Pipeline */}
              {data.pipeline.length > 0 && (
                <Grid.Col span={{ base: 12, lg: data.contracts ? 7 : 12 }}>
                  <SectionCard title="Procurement pipeline">
                    <Stack gap="lg">
                      {data.pipeline.map((p) => (
                        <PipelineRow key={p.key} label={p.label} path={p.path} statuses={p.statuses} />
                      ))}
                    </Stack>
                  </SectionCard>
                </Grid.Col>
              )}

              {/* Contracts expiring */}
              {data.contracts && (
                <Grid.Col span={{ base: 12, lg: data.pipeline.length > 0 ? 5 : 12 }}>
                  <SectionCard title="Contracts ending soon" action={{ label: "All contracts", to: "/contracts" }}>
                    {data.contracts.expiring.length === 0 ? (
                      <EmptyState icon={IconCircleCheck} message="No active contracts end in the next 90 days." />
                    ) : (
                      <Stack gap="sm">
                        {data.contracts.expiring.map((c) => {
                          const pct = c.contractValue > 0 ? (c.consumedValue / c.contractValue) * 100 : 0;
                          const urgency = (c.daysLeft ?? 999) <= 30 ? "red" : (c.daysLeft ?? 999) <= 60 ? "orange" : "yellow";
                          return (
                            <Stack
                              key={c.id}
                              renderRoot={(props) => <Link to={`/contracts/${c.id}`} {...props} />}
                              gap={6}
                              p="xs"
                              className="rounded-md no-underline transition-colors duration-150 hover:bg-[var(--mantine-color-default-hover)]"
                              style={{ color: "inherit" }}
                            >
                              <Group justify="space-between" wrap="nowrap" gap="sm">
                                <Stack gap={0} style={{ minWidth: 0 }}>
                                  <Text size="sm" fw={600} lineClamp={1}>
                                    {c.title || c.contractNumber}
                                  </Text>
                                  <Text size="xs" c="dimmed" lineClamp={1}>
                                    {c.contractNumber} · {c.supplierName}
                                  </Text>
                                </Stack>
                                <Badge color={urgency} variant="light" radius="sm" style={{ ...tabular, flexShrink: 0 }}>
                                  {c.daysLeft} {c.daysLeft === 1 ? "day" : "days"}
                                </Badge>
                              </Group>
                              <Progress value={pct} size="xs" color="blue" aria-label={`${pct.toFixed(0)}% of contract value consumed`} />
                              <Text size="xs" c="dimmed" style={tabular}>
                                {formatCurrency(c.consumedValue)} of {formatCurrency(c.contractValue)} used · ends{" "}
                                {dayjs(c.endDate).format("D MMM YYYY")}
                              </Text>
                            </Stack>
                          );
                        })}
                      </Stack>
                    )}
                  </SectionCard>
                </Grid.Col>
              )}

              {/* Recent activity */}
              <Grid.Col span={12}>
                <SectionCard title="Recent activity">
                  {data.recentActivity.length === 0 ? (
                    <EmptyState icon={IconInbox} message="No activity yet." />
                  ) : (
                    <Table.ScrollContainer minWidth={640}>
                      <Table highlightOnHover verticalSpacing="sm">
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Record</Table.Th>
                            <Table.Th>Title</Table.Th>
                            <Table.Th>Status</Table.Th>
                            <Table.Th ta="right">Updated</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {data.recentActivity.map((a) => (
                            <Table.Tr key={`${a.type}-${a.id}`}>
                              <Table.Td>
                                <Text size="xs" c="dimmed">
                                  {a.type}
                                </Text>
                                <Anchor component={Link} to={a.path} size="sm" fw={600}>
                                  {a.reference}
                                </Anchor>
                              </Table.Td>
                              <Table.Td maw={420}>
                                <Text size="sm" lineClamp={1} style={dhivehiDisplayStyle(a.title)}>
                                  {a.title}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <StatusBadge status={a.status} />
                              </Table.Td>
                              <Table.Td ta="right">
                                <Tooltip label={dayjs(a.updatedAt).format("D MMM YYYY, HH:mm")}>
                                  <Text size="sm" c="dimmed" span>
                                    {dayjs(a.updatedAt).fromNow()}
                                  </Text>
                                </Tooltip>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                  )}
                </SectionCard>
              </Grid.Col>
            </Grid>
          </>
        )}
      </Stack>
    </Container>
  );
};
